import * as React from "react";
import { reportDisplay, toReportNumber } from './reportNumbers';
import { DataGrid, GridToolbar, useGridApiRef, gridFilteredSortedRowIdsSelector } from "@mui/x-data-grid";
import { ReportExportContext } from './ReportExportContext';

import {
  Alert,
  Box,
  Card,
  CardContent,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";

import { exportTableToPDF } from "./ExportTablePDF";
import { exportTableToExcel } from "./ExportTableData";
import ExportReportDialog from './ExportReportDialog';

export default function Table({
  rows = [],
  columns: suppliedColumns = [],

  title = "Report",
  showTitle = true,

  isDark = false,

  searchable = false,
  search = "",
  onSearchChange,
  searchPlaceholder = "Search...",

  exportable = false,
  exportTitle,
  exportFileName,
  exportDateRange,
  exportDisabled = false,
  exportHelpText,

  getRowId = (row) => row.id,

  height = 600,
  pageSize = 50,
  rowsPerPageOptions = [25, 50, 100],
  showToolbar = true,

  className = "",
}) {
  const apiRef = useGridApiRef();
  const reportScope = React.useContext(ReportExportContext);
  const exportBlocked = reportScope.disabled || exportDisabled;
  const [exportSnapshot, setExportSnapshot] = React.useState(null);
  React.useEffect(() => { if (exportBlocked) setExportFormat(null); }, [exportBlocked]);
  // The grid selector includes column/quick filters and sorting, but does not limit rows to the current page.
  const matchingRows = () => gridFilteredSortedRowIdsSelector(apiRef).map(id => ({ ...apiRef.current.getRow(id) }));
  const openExport = format => {
    if (exportBlocked) return;
    const filterItems = apiRef.current.state.filter.filterModel.items;
    const sorts = apiRef.current.state.sorting.sortModel;
    const columnName = field => suppliedColumns.find(column => column.field === field)?.headerName || field;
    const notes = [
      ...(currentSearch ? [`Search: ${currentSearch}`] : []),
      ...filterItems.map(item => `Column filter: ${columnName(item.field)} ${item.operator} ${item.value ?? ''}`),
      ...sorts.map(item => `Sort: ${columnName(item.field)} ${item.sort}`),
      ...(exportHelpText ? [exportHelpText] : []),
    ];
    setExportSnapshot({ rows: matchingRows(), dates: { ...(exportDateRange || reportScope.filters) }, notes, scopeSummary: reportScope.summary });
    setExportFormat(format);
  };
  // Keep numeric sorting intact while displaying unavailable cells consistently.
  const columns = React.useMemo(() => suppliedColumns.map(column => ({
    ...column,
    valueFormatter: (value, ...args) => column.type === 'number' && toReportNumber(value) == null ? '-' : reportDisplay(
      column.valueFormatter ? column.valueFormatter(value, ...args) : value
    ),
    ...(column.renderCell ? { renderCell: params => {
      if (column.type === 'number' && toReportNumber(params.value) == null) return '-';
      if (typeof params.value === 'number' && !Number.isFinite(params.value)) return '-';
      return reportDisplay(column.renderCell(params));
    } } : {}),
  })), [suppliedColumns]);
  /*
   * ------------------------------------------------------------
   * Internal search state
   *
   * If the parent supplies search/onSearchChange,
   * those are used instead.
   *
   * Otherwise the table manages its own search.
   * ------------------------------------------------------------
   */
  const [internalSearch, setInternalSearch] =
    React.useState("");
  const [exportBusy, setExportBusy] = React.useState(false);
  const [exportError, setExportError] = React.useState("");
  const exporting = React.useRef(false);
  const [exportFormat, setExportFormat] = React.useState(null);

  const currentSearch =
    onSearchChange !== undefined
      ? search
      : internalSearch;

  const handleSearchChange = (value) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearch(value);
    }
  };

  /*
   * ------------------------------------------------------------
   * Filter rows
   *
   * Search across all visible column values.
   * ------------------------------------------------------------
   */
  const filteredRows = React.useMemo(() => {
    if (!searchable || !currentSearch.trim()) {
      return rows;
    }

    const searchValue =
      currentSearch.trim().toLowerCase();

    return rows.filter((row) =>
      columns.some((column) => {
        const value = row?.[column.field];

        if (
          value === null ||
          value === undefined
        ) {
          return false;
        }

        return String(value)
          .toLowerCase()
          .includes(searchValue);
      })
    );
  }, [
    rows,
    columns,
    searchable,
    currentSearch,
  ]);

  /*
   * ------------------------------------------------------------
   * PDF Export
   * ------------------------------------------------------------
   */
  const handleExportPDF = () => {
    if (exportDateRange) { openExport('pdf'); return; }
    if (exportBlocked) return;
    const defaultName =
      exportFileName ||
      `${exportTitle || title || "Report"} Report`;

    const fileName = window.prompt(
      "Enter a name for your PDF report:",
      defaultName
    );

    if (fileName === null) {
      return;
    }

    const trimmedName = fileName.trim();

    if (!trimmedName) {
      return;
    }

    exportTableToPDF({
      rows: matchingRows(),
      columns,
      title:
        exportTitle ||
        title ||
        "Report",
      fileName: trimmedName,
    });
  };

  // Export all search-matching rows, not just the visible DataGrid page.
  const handleExportExcel = async () => {
    if (exportDateRange) { openExport('excel'); return; }
    if (exportBlocked) return;
    if (exporting.current) return;
    const defaultName = exportFileName || `${exportTitle || title || "Report"} Report`;
    const fileName = window.prompt("Enter a name for your Excel report:", defaultName);
    if (!fileName?.trim()) return;
    exporting.current = true;
    setExportBusy(true);
    setExportError("");
    try {
      await exportTableToExcel({
        rows: matchingRows(),
        columns,
        title: exportTitle || title || "Report",
        fileName: fileName.trim(),
      });
    } catch (error) {
      setExportError(error.message || "Export failed. Try a smaller report range.");
    } finally {
      exporting.current = false;
      setExportBusy(false);
    }
  };

  return (
    <Box
      className={`${className} min-w-0`}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Card
        variant="outlined"
        className="reports-panel reports-table rounded-lg shadow-sm"
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",

          backgroundColor: isDark
            ? "#111827"
            : "#ffffff",

          borderColor: isDark
            ? "#374151"
            : "#e5e7eb",
        }}
      >
        {/* HEADER */}
        {(showTitle ||
          searchable ||
          exportable) && (
          <CardContent className="reports-table-heading !p-4 !pb-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 min-w-0">

              {/* TITLE */}
              {showTitle && (
                <Typography
                  component="h3"
                  variant="subtitle1"
                  className="reports-panel-title text-blue-700 dark:text-blue-300 font-semibold text-lg"
                  sx={{
                    minWidth: 0,
                    flexShrink: 1,
                  }}
                >
                  {title}
                </Typography>
              )}

              {/* SEARCH + EXPORT */}
              <div className="flex flex-wrap items-center gap-[15px] min-w-0">

                {/* SEARCH */}
                {searchable && (
                  <TextField
                    size="small"
                    placeholder={
                      searchPlaceholder
                    }
                    value={currentSearch}
                    onChange={(event) =>
                      handleSearchChange(
                        event.target.value
                      )
                    }
                    sx={{
                      width: {
                        xs: "100%",
                        sm: 260,
                      },

                      flexShrink: 1,

                      "& .MuiInputBase-root":
                        {
                          backgroundColor:
                            isDark
                              ? "#1f2937"
                              : "#ffffff",

                          color: isDark
                            ? "#f3f4f6"
                            : "#111827",
                        },

                      "& .MuiInputBase-input::placeholder":
                        {
                          color: isDark
                            ? "#9ca3af"
                            : "#6b7280",

                          opacity: 1,
                        },

                      "& .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: isDark
                            ? "#4b5563"
                            : "#d1d5db",
                        },

                      "&:hover .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: isDark
                            ? "#6b7280"
                            : "#9ca3af",
                        },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor:
                            "#1976d2",
                        },
                    }}
                  />
                )}

                {/* EXPORT PDF */}
                {exportable && (
                  <Tooltip title={exportBlocked ? 'Export is available when report loading finishes successfully.' : 'Export PDF'}>
                    <span>
                    <IconButton
                      onClick={
                        handleExportPDF
                      }
                      aria-label="Export PDF"
                      disabled={exportBlocked || exportBusy}
                      sx={{
                        flexShrink: 0,

                        color: isDark
                          ? "#90caf9"
                          : "#1976d2",
                      }}
                    >
                      <PictureAsPdfIcon />
                    </IconButton>
                    </span>
                  </Tooltip>
                )}
                {/* Excel uses the same row set as the PDF export. */}
                {exportable && (
                  <Tooltip title={exportBlocked ? 'Export is available when report loading finishes successfully.' : exportBusy ? "Preparing export…" : "Export Excel"}>
                    <span>
                      <IconButton
                        aria-label="Export Excel"
                        disabled={exportBusy || exportBlocked}
                        onClick={handleExportExcel}
                        sx={{ flexShrink: 0, color: isDark ? "#90caf9" : "#1976d2" }}
                      >
                        <TableChartOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>
          </CardContent>
        )}

        {exportBusy && <Typography role="status" sx={{ px: 2, pb: 1 }}>Preparing export…</Typography>}
        {exportError && <Alert severity="error" onClose={() => setExportError("")}>{exportError}</Alert>}

        {exportFormat && exportSnapshot && !exportBlocked && <ExportReportDialog format={exportFormat} title={exportTitle || title}
          defaultFileName={exportFileName || `${exportTitle || title} Report`} initialDates={exportSnapshot.dates}
          columns={columns} loadRows={async () => ({ rows: exportSnapshot.rows, notes: exportSnapshot.notes })}
          scopeSummary={exportSnapshot.scopeSummary} isDark={isDark}
          helpText={`${exportSnapshot.rows.length.toLocaleString()} matching rows will be exported.${exportHelpText ? ` ${exportHelpText}` : ''}`}
          onClose={() => {
            if (exportBusy) return;
            setExportFormat(null);
            setExportSnapshot(null);
          }} />}

        {/* DATA GRID */}
        <Box
          sx={{
            height,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <DataGrid
            apiRef={apiRef}
            rowHeight={44}
            columnHeaderHeight={44}
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}

            initialState={{
              pagination: {
                paginationModel: {
                  pageSize,
                  page: 0,
                },
              },
            }}

            pageSizeOptions={
              rowsPerPageOptions
            }

            disableRowSelectionOnClick

            slotProps={{ toolbar: { csvOptions: { disableToolbarButton: true }, printOptions: { disableToolbarButton: true } } }}

            slots={
              showToolbar
                ? {
                    toolbar: GridToolbar,
                  }
                : undefined
            }

            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,

              boxSizing: "border-box",

              /* Main grid */
              backgroundColor: isDark
                ? "#1f2937"
                : "#ffffff",

              color: isDark
                ? "#e5e7eb"
                : "#111827",

              borderColor: isDark
                ? "#374151"
                : "#e5e7eb",

              /* Prevent grid overflow */
              "& .MuiDataGrid-main": {
                minWidth: 0,
                maxWidth: "100%",
              },

              /* Column headers */
              "& .MuiDataGrid-columnHeaders":
                {
                  backgroundColor: isDark
                    ? "#374151"
                    : "#f4f4f4",

                  fontWeight: "bold",

                  color: isDark
                    ? "#f3f4f6"
                    : "#111827",

                  borderBottomColor: isDark
                    ? "#4b5563"
                    : "#e5e7eb",
                },

              "& .MuiDataGrid-columnHeader":
                {
                  backgroundColor: isDark
                    ? "#374151"
                    : "#f4f4f4",
                },

              "& .MuiDataGrid-columnHeaderTitle":
                {
                  color: isDark
                    ? "#f9fafb"
                    : "#111827",

                  fontWeight: 700,
                },

              /* Header separator */
              "& .MuiDataGrid-iconSeparator":
                {
                  color: isDark
                    ? "#6b7280"
                    : "#d1d5db",
                },

              /* Cells */
              "& .MuiDataGrid-cell": {
                borderBottomColor: isDark
                  ? "#374151"
                  : "#e5e7eb",

                color: isDark
                  ? "#e5e7eb"
                  : "#111827",
              },

              /* Hover */
              "& .MuiDataGrid-row:hover": {
                backgroundColor: isDark
                  ? "#111827"
                  : "#f9fafb",
              },

              /* Selected */
              "& .MuiDataGrid-row.Mui-selected":
                {
                  backgroundColor: isDark
                    ? "#334155"
                    : "#DBEAFE",
                },

              "& .MuiDataGrid-row.Mui-selected:hover":
                {
                  backgroundColor: isDark
                    ? "#3b4a62"
                    : "#BFDBFE",
                },

              /* Footer */
              "& .MuiDataGrid-footerContainer":
                {
                  borderTopColor: isDark
                    ? "#374151"
                    : "#e5e7eb",

                  backgroundColor: isDark
                    ? "#1f2937"
                    : "#ffffff",
                },

              /* Pagination */
              "& .MuiTablePagination-root":
                {
                  color: isDark
                    ? "#d1d5db"
                    : "#374151",
                },

              "& .MuiTablePagination-selectLabel":
                {
                  color: isDark
                    ? "#d1d5db"
                    : "#374151",
                },

              "& .MuiTablePagination-displayedRows":
                {
                  color: isDark
                    ? "#d1d5db"
                    : "#374151",
                },

              /* Icons */
              "& .MuiSvgIcon-root": {
                color: isDark
                  ? "#d1d5db"
                  : "#374151",
              },

              /* Toolbar */
              "& .MuiDataGrid-toolbarContainer":
                {
                  backgroundColor: isDark
                    ? "#1f2937"
                    : "#ffffff",
                },

              "& .MuiDataGrid-toolbarContainer button":
                {
                  color: isDark
                    ? "#d1d5db"
                    : "#374151",
                },

              /* Toolbar inputs */
              "& .MuiDataGrid-toolbarContainer .MuiInputBase-root":
                {
                  color: isDark
                    ? "#e5e7eb"
                    : "#111827",
                },

              "& .MuiDataGrid-toolbarContainer .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: isDark
                    ? "#4b5563"
                    : "#d1d5db",
                },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
}
